import { Step } from 'react-joyride';
import { Reducer } from 'redux';
import { action } from 'typesafe-actions';
import { Language } from '../constants/Languages';
import { LocalStorage } from '../constants/LocalStorage';
import { defaultVisibleColumns, MenuStatus, TableFilterType, ToastMessageType } from '../constants/Viewer';
import { CustomFilterType, GroupFilter, SelectedFilterValues } from '../types/CustomFilter';
import { Theme, ViewerState } from '../types/ViewerState';

/* global localStorage */

// Action types

export enum ViewerActionTypes {
  SetToastMessage = 'SET_TOAST_MESSAGE',
  SetCurrency = 'SET_CURRENCY',
  SetUseMetricUnits = 'SET_USE_METRIC_UNITS',
  SetTheme = 'SET_THEME',
  GetTheme = 'GET_THEME',
  GetThemeComplete = 'GET_THEME_COMPLETE',
  FetchVisibleColumns = 'FETCH_VISIBLE_COLUMNS',
  FetchVisibleColumnsError = 'FETCH_VISIBLE_COLUMNS_ERROR',
  FetchVisibleColumnsSuccess = 'FETCH_VISIBLE_COLUMNS_SUCCESS',
  SetVisibleColumns = 'SET_VISIBLE_COLUMNS',
  SetVisibleColumnsError = 'SET_VISIBLE_COLUMNS_ERROR',
  SetVisibleColumnsSuccess = 'SET_VISIBLE_COLUMNS_SUCCESS',
  SetWarningText = 'SET_WARNING_TEXT',
  ClearWarningText = 'CLEAR_WARNING_TEXT',
  SetTourSteps = 'SET_TOUR_STEPS',
  OpenTour = 'OPEN_TOUR',
  TourNextOrPrevious = 'TOUR_NEXT_OR_PREVIOUS',
  EndTour = 'END_TOUR',
  SetTourViewed = 'SET_TOUR_VIEWED',
  SetFilterDialog = 'SET_FILTER_DIALOG',
  SetFilter = 'SET_FILTER',
  SetGroupFilters = 'SET_GROUP_FILTERS',
  SetGroupFiltersSuccess = 'SET_GROUP_FILTERS_SUCCESS',
  FetchGroupFilters = 'FETCH_GROUP_FILTERS',
  FetchGroupFiltersSuccess = 'FETCH_GROUP_FILTERS_SUCCESS',
  ResetFilter = 'RESET_FILTER',
  ClearFilters = 'CLEAR_FILTERS',
  ClearFiltersSuccess = 'CLEAR_FILTERS_SUCCESS',
  SetSelectedClientId = 'SET_SELECTED_CLIENT_ID',
  SetDefaultClientId = 'SET_DEFAULT_CLIENT_ID',
  SetSelectedTabId = 'SET_SELECTED_TAB_ID',
  SetSelectedPricingTabId = 'SET_SELECTED_PRICING_TAB_ID',
  ResetClientIds = 'RESET_CLIENT_IDS',
  GetVisibleLanguages = 'GET_VISIBLE_LANGUAGES',
  SetVisibleLanguages = 'SET_VISIBLE_LANGUAGES',
  SetMenuStatus = 'SET_MENU_STATUS',
  SetPublishBarDisplay = 'SET_PUBLISH_BAR_DISPLAY',
}

// Reducer

export const INITIAL_VIEWER_STATE: ViewerState = {
  menuStatus: MenuStatus.Expanded,
  displayPublishBar: false,
  visibleColumns: [],
  warning: '',
  tourRun: false,
  tourSteps: [],
  tourStepIndex: 0,
  groupFilters: [],
  tableFilterType: TableFilterType.Order,
  filterType: '',
  availableFilterValues: [],
  selectedFilterValues: [],
  languages: [],
};

export const viewerReducer: Reducer<ViewerState> = (state = INITIAL_VIEWER_STATE, viewerAction) => {
  switch (viewerAction.type) {
    case ViewerActionTypes.SetTheme: {
      const {
        payload: { theme },
      } = viewerAction;
      return {
        ...state,
        theme: {
          ...theme,
        },
      };
    }

    case ViewerActionTypes.FetchVisibleColumns: {
      return { ...state, visibleColumns: [], loading: true };
    }

    case ViewerActionTypes.FetchVisibleColumnsError: {
      return { ...state, visibleColumns: defaultVisibleColumns, loading: false };
    }

    case ViewerActionTypes.FetchVisibleColumnsSuccess: {
      const {
        payload: { visibleColumns },
      } = viewerAction;

      return { ...state, visibleColumns, loading: false };
    }

    case ViewerActionTypes.SetVisibleColumns: {
      const {
        payload: { visibleColumns },
      } = viewerAction;

      return { ...state, visibleColumns };
    }

    case ViewerActionTypes.SetVisibleColumnsError: {
      return { ...state, visibleColumns: defaultVisibleColumns, loading: false };
    }

    case ViewerActionTypes.SetVisibleColumnsSuccess: {
      const {
        payload: { visibleColumns },
      } = viewerAction;

      return { ...state, visibleColumns, loading: false };
    }

    case ViewerActionTypes.SetWarningText: {
      const {
        payload: { warning },
      } = viewerAction;

      return { ...state, warning };
    }

    case ViewerActionTypes.ClearWarningText: {
      return { ...state, warning: '' };
    }

    case ViewerActionTypes.SetTourSteps: {
      const {
        payload: { tourSteps },
      } = viewerAction;

      return { ...state, tourSteps };
    }

    case ViewerActionTypes.OpenTour: {
      return { ...state, tourRun: true };
    }

    case ViewerActionTypes.TourNextOrPrevious: {
      const {
        payload: { tourStepIndex },
      } = viewerAction;

      return { ...state, tourStepIndex };
    }

    case ViewerActionTypes.EndTour: {
      localStorage.setItem(LocalStorage.TourIndex, '1');
      return { ...state, tourRun: false };
    }

    case ViewerActionTypes.SetFilterDialog: {
      const {
        payload: { groupId, tableFilterType, filterType, availableFilterValues },
      } = viewerAction;

      let selectedFilterValues: SelectedFilterValues[] = [];
      const groupFilters = state.groupFilters.find((groupFilter) => groupFilter.groupId === groupId);
      const tableFiltersOfType = groupFilters
        ? groupFilters.tableFilters.filter((tableFilter) => tableFilter.tableFilterType === tableFilterType) || []
        : [];
      if (tableFiltersOfType.length > 0 && tableFiltersOfType[0].filters) {
        const filtersOfType = tableFiltersOfType[0].filters.filter((filter) => filter.filterType === filterType) || [];
        if (filtersOfType[0] && filtersOfType[0].selectedFilterValues) {
          selectedFilterValues = filtersOfType[0].selectedFilterValues;
        }
      }
      return { ...state, tableFilterType, filterType, availableFilterValues, selectedFilterValues, loading: false };
    }

    case ViewerActionTypes.SetFilter: {
      const {
        payload: { selectedFilterValues = [] },
      } = viewerAction;

      return {
        ...state,
        selectedFilterValues,
      };
    }

    case ViewerActionTypes.SetGroupFiltersSuccess:
    case ViewerActionTypes.FetchGroupFiltersSuccess:
    case ViewerActionTypes.ClearFiltersSuccess: {
      const {
        payload: { groupFilters = [] },
      } = viewerAction;
      return { ...state, groupFilters };
    }

    case ViewerActionTypes.ResetFilter: {
      return {
        ...state,
        filterType: '',
        selectedFilterValues: [],
        loading: false,
      };
    }

    case ViewerActionTypes.ClearFilters: {
      const {
        payload: { tableFilterType },
      } = viewerAction;
      return {
        ...state,
        tableFilterType,
        filterType: '',
        selectedFilterValues: [],
      };
    }

    case ViewerActionTypes.SetSelectedClientId: {
      const {
        payload: { selectedClientId },
      } = viewerAction;
      return {
        ...state,
        selectedClientId,
      };
    }

    case ViewerActionTypes.SetDefaultClientId: {
      const {
        payload: { defaultClientId },
      } = viewerAction;
      return {
        ...state,
        defaultClientId,
      };
    }

    case ViewerActionTypes.SetSelectedTabId: {
      const {
        payload: { selectedTabId },
      } = viewerAction;
      return {
        ...state,
        selectedTabId,
      };
    }

    case ViewerActionTypes.SetSelectedPricingTabId: {
      const {
        payload: { selectedPricingTabId },
      } = viewerAction;
      return {
        ...state,
        selectedPricingTabId,
      };
    }

    case ViewerActionTypes.ResetClientIds: {
      return {
        ...state,
        defaultClientId: undefined,
        selectedClientId: undefined,
        selectedTabId: undefined,
      };
    }

    case ViewerActionTypes.SetVisibleLanguages: {
      const {
        payload: { languages },
      } = viewerAction;
      return {
        ...state,
        languages,
      };
    }

    case ViewerActionTypes.SetCurrency: {
      const {
        payload: { currency },
      } = viewerAction;
      return {
        ...state,
        currency,
      };
    }

    case ViewerActionTypes.SetUseMetricUnits: {
      const {
        payload: { useMetricUnits },
      } = viewerAction;
      return {
        ...state,
        useMetricUnits,
      };
    }

    case ViewerActionTypes.SetMenuStatus: {
      const {
        payload: { menuStatus },
      } = viewerAction;
      return {
        ...state,
        menuStatus,
      };
    }

    case ViewerActionTypes.SetPublishBarDisplay: {
      const {
        payload: { displayPublishBar },
      } = viewerAction;
      return {
        ...state,
        displayPublishBar,
      };
    }

    default: {
      return state;
    }
  }
};

// Action creators

export type ViewerActionType = { type: ViewerActionTypes };

export const setToastMessage = (message: string, type?: ToastMessageType): ViewerActionType =>
  action(ViewerActionTypes.SetToastMessage, { message, type });

export const setTheme = (theme: Theme): ViewerActionType => action(ViewerActionTypes.SetTheme, { theme });

export const setCurrency = (currency?: string): ViewerActionType => action(ViewerActionTypes.SetCurrency, { currency });

export const setUseMetricUnits = (useMetricUnits?: boolean): ViewerActionType =>
  action(ViewerActionTypes.SetUseMetricUnits, { useMetricUnits });

export const getTheme = (): ViewerActionType => action(ViewerActionTypes.GetTheme);

export const getThemeComplete = (): ViewerActionType => action(ViewerActionTypes.GetThemeComplete);

export const fetchVisibleColumns = (): ViewerActionType => action(ViewerActionTypes.FetchVisibleColumns);

export const fetchVisibleColumnsError = (): ViewerActionType => action(ViewerActionTypes.FetchVisibleColumnsError);

export const fetchVisibleColumnsSuccess = (visibleColumns: string[]): ViewerActionType =>
  action(ViewerActionTypes.FetchVisibleColumnsSuccess, { visibleColumns });

export const setVisibleColumns = (visibleColumns?: string[]): ViewerActionType =>
  action(ViewerActionTypes.SetVisibleColumns, { visibleColumns });

export const setVisibleColumnsError = (): ViewerActionType => action(ViewerActionTypes.SetVisibleColumnsError);

export const setVisibleColumnsSuccess = (visibleColumns: string[]): ViewerActionType =>
  action(ViewerActionTypes.SetVisibleColumnsSuccess, { visibleColumns });

export const setWarningText = (warning: string): ViewerActionType =>
  action(ViewerActionTypes.SetWarningText, { warning });

export const clearWarningText = (): ViewerActionType => action(ViewerActionTypes.ClearWarningText);

export const setTourSteps = (tourSteps: Step[]): ViewerActionType =>
  action(ViewerActionTypes.SetTourSteps, { tourSteps });

export const openTour = (): ViewerActionType => action(ViewerActionTypes.OpenTour);

export const tourNextOrPrevious = (tourStepIndex: number): ViewerActionType =>
  action(ViewerActionTypes.TourNextOrPrevious, { tourStepIndex });

export const endTour = (): ViewerActionType => action(ViewerActionTypes.EndTour);

export const setFilterDialog = (
  groupId: string | undefined,
  tableFilterType: TableFilterType,
  filterType?: string,
  availableFilterValues?: CustomFilterType,
): ViewerActionType =>
  action(ViewerActionTypes.SetFilterDialog, { groupId, tableFilterType, filterType, availableFilterValues });

export const setFilter = (selectedFilterValues: SelectedFilterValues[]): ViewerActionType =>
  action(ViewerActionTypes.SetFilter, { selectedFilterValues });

export const setTableFilter = (): ViewerActionType => action(ViewerActionTypes.SetGroupFilters);

export const setGroupFilterSuccess = (groupFilters: GroupFilter[]): ViewerActionType =>
  action(ViewerActionTypes.SetGroupFiltersSuccess, { groupFilters });

export const fetchGroupFilter = (): ViewerActionType => action(ViewerActionTypes.FetchGroupFilters);

export const fetchGroupFilterSuccess = (groupFilters: GroupFilter[]): ViewerActionType =>
  action(ViewerActionTypes.FetchGroupFiltersSuccess, { groupFilters });

export const resetFilter = (): ViewerActionType => action(ViewerActionTypes.ResetFilter);

export const clearFilters = (tableFilterType: TableFilterType): ViewerActionType =>
  action(ViewerActionTypes.ClearFilters, { tableFilterType });

export const clearFiltersSuccess = (groupFilters: GroupFilter[]): ViewerActionType =>
  action(ViewerActionTypes.SetGroupFiltersSuccess, { groupFilters });

export const setSelectedClientId = (selectedClientId: string): ViewerActionType =>
  action(ViewerActionTypes.SetSelectedClientId, { selectedClientId });

export const setDefaultClientId = (defaultClientId: string): ViewerActionType =>
  action(ViewerActionTypes.SetDefaultClientId, { defaultClientId });

export const setSelectedTabId = (selectedTabId: string): ViewerActionType =>
  action(ViewerActionTypes.SetSelectedTabId, { selectedTabId });

export const setSelectedPricingTabId = (selectedPricingTabId: string | undefined): ViewerActionType =>
  action(ViewerActionTypes.SetSelectedPricingTabId, { selectedPricingTabId });

export const resetClientIds = (): ViewerActionType => action(ViewerActionTypes.ResetClientIds);

export const getVisibleLanguages = (vendorLanguages: string[]): ViewerActionType =>
  action(ViewerActionTypes.GetVisibleLanguages, { vendorLanguages });

export const setVisibleLanguages = (languages: Language[]): ViewerActionType =>
  action(ViewerActionTypes.SetVisibleLanguages, { languages });

export const setMenuStatus = (menuStatus: MenuStatus): ViewerActionType =>
  action(ViewerActionTypes.SetMenuStatus, { menuStatus });

export const setPublishBarDisplay = (displayPublishBar: boolean): ViewerActionType =>
  action(ViewerActionTypes.SetPublishBarDisplay, { displayPublishBar });
